import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Heart, Stethoscope, Eye, Scissors } from "lucide-react";
import { COPY } from "@/content/copy";
import WaitlistForm from "@/components/WaitlistForm";

const PayMonthlyHealthcare = () => {
  return (
    <>
      <Helmet>
        <title>Pay Monthly for Healthcare | BNPL Medical Treatment UAE | Flexaura</title>
        <meta name="description" content="Pay monthly for healthcare & medical treatments in UAE. BNPL for dental, cosmetic surgery, IVF, procedures. Healthcare financing AED 2K-200K+. Treatment now, pay later." />
        <meta name="keywords" content="pay monthly healthcare UAE, BNPL healthcare, buy now pay later medical, healthcare financing UAE, pay monthly dental UAE, cosmetic surgery BNPL, IVF financing UAE, medical treatment installments Dubai, Abu Dhabi healthcare BNPL" />
        <link rel="canonical" href="https://flexaura.me/pay-monthly-healthcare" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Pay Monthly for Healthcare | BNPL Medical UAE", 
            "description": "Pay monthly for healthcare and medical treatments in UAE with flexible BNPL and installment options from AED 2,000 to 200,000+",
            "url": "https://flexaura.me/pay-monthly-healthcare",
            "mainEntity": {
              "@type": "FinancialService",
              "name": "Healthcare Payment Plans",
              "description": "Monthly payment options for medical treatments including dental, cosmetic surgery, and healthcare procedures",
              "serviceType": ["Buy Now Pay Later", "Healthcare Financing", "Monthly Healthcare Payments"],
              "areaServed": { "@type": "Country", "name": "United Arab Emirates" }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        <Breadcrumbs />
        
        <main>
          <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
                <Heart className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-primary">Healthcare BNPL</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                Pay Monthly for Healthcare in UAE
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {COPY.sublineByVertical.healthcare}
              </p>

              <WaitlistForm
                source="pay-monthly-healthcare"
                placeholder="Get early access to healthcare BNPL..."
                buttonText="Join Waitlist"
                className="max-w-md mx-auto mb-12"
              />
            </div>
          </section>

          {/* Healthcare Options */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">Pay Monthly for Essential Healthcare Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { icon: Stethoscope, title: "Medical Procedures", description: "Surgeries, treatments, and specialized medical care" },
                  { icon: Eye, title: "Dental & Vision", description: "Dental implants, orthodontics, LASIK, and eye procedures" },
                  { icon: Scissors, title: "Cosmetic Surgery", description: "Aesthetic procedures, plastic surgery, and beauty treatments" },
                  { icon: Heart, title: "Fertility & IVF", description: "IVF treatments, fertility procedures, and family planning" }
                ].map((item, index) => (
                  <div key={index} className="text-center p-6 rounded-2xl card-shadow bg-card">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-16 px-4 bg-muted/50">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-heading font-bold mb-12">Why Choose Healthcare BNPL?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  "Get treatment immediately without waiting or compromising on quality",
                  "Access premium healthcare services with manageable monthly payments", 
                  "Plan your healthcare expenses with predictable installment amounts"
                ].map((benefit, index) => (
                  <div key={index} className="p-6 bg-card rounded-xl">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 text-primary font-bold text-xl">✓</div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-12">Healthcare BNPL FAQ</h2>
              <div className="space-y-6">
                {[
                  { q: "Can I pay monthly for dental treatments in UAE?", a: "Yes, Flexaura offers monthly payment plans for dental treatments including implants, orthodontics, and cosmetic dentistry at leading clinics across Dubai, Abu Dhabi, and the UAE." },
                  { q: "Is BNPL available for cosmetic surgery in UAE?", a: "Absolutely. We partner with certified cosmetic surgery clinics to offer flexible payment plans for aesthetic procedures, allowing you to pay monthly while receiving treatment from qualified professionals." },
                  { q: "What healthcare providers accept Flexaura payments?", a: "We work with leading hospitals, clinics, and medical centers across the UAE. Our network includes both public and private healthcare providers offering various specialties and treatments." }
                ].map((faq, index) => (
                  <div key={index} className="border-b pb-4">
                    <h3 className="font-semibold mb-2 text-lg">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default PayMonthlyHealthcare;
